import { NavigatorLockAcquireTimeoutError, navigatorLock } from '@supabase/supabase-js';

/**
 * Supabase's own auth lock (navigatorLock), made safe when the lock is stolen.
 *
 * What happens: every auth call takes one Web Lock. On a slow network a call
 * can hold it for over 5 s; the next call then gives up waiting and STEALS
 * it. Per the Web Locks spec the first call's work keeps running to the end
 * — only its exclusivity is lost — but its promise is rejected anyway:
 *   - raw "AbortError: Lock broken by another request with the 'steal'
 *     option" (auth-js 2.104 leaks it for timeout-0 / steal-back requests),
 *   - or "Lock … was released because another request stole it".
 * Nobody catches those: our onboarding's `void updateUser(...)`, and the
 * fire-and-forget initial-session emit inside onAuthStateChange. Sentry:
 * JAVASCRIPT-REACT-4 (10-03), -E and -F (10-09, after the first version of
 * this file, which only renamed the error).
 *
 * Fix: if the lock is stolen after our work started, wait for that work and
 * return its result — the call did succeed. It never runs twice and never
 * steals back. A lock we never got (no work started) is still a real
 * acquire timeout and is reported as one, which the refresh tick ignores.
 *
 * Review this file when @supabase/supabase-js is upgraded (2.117 converts
 * the raw AbortError upstream, but still rejects a stolen call).
 */
export async function authLock<R>(
  name: string,
  acquireTimeout: number,
  fn: () => Promise<R>,
): Promise<R> {
  // Same choice GoTrueClient makes without a custom lock: no Web Locks API
  // (old browsers, tests) → run unlocked.
  if (typeof navigator === 'undefined' || !navigator.locks) return fn();

  // A holder object, not a `let`: TS would narrow a closure-assigned `let`
  // to its initial null inside the catch below.
  const work: { running: Promise<R> | null } = { running: null };
  const tracked = () => {
    work.running = fn();
    return work.running;
  };

  try {
    return await navigatorLock(name, acquireTimeout, tracked);
  } catch (e) {
    // Our work started and is still going (or done): its result is the answer.
    if (work.running && isStolen(e)) return await work.running;
    if (isAbortError(e)) {
      throw new NavigatorLockAcquireTimeoutError(
        `Lock "${name}" was released because another request stole it`,
      );
    }
    throw e;
  }
}

function isStolen(e: unknown): boolean {
  if (isAbortError(e)) return true;
  return (
    e instanceof Error &&
    (e as { isAcquireTimeout?: boolean }).isAcquireTimeout === true &&
    /another request stole it/.test(e.message)
  );
}

/** DOMException does not extend Error everywhere — check the shape. */
function isAbortError(e: unknown): boolean {
  return e !== null && typeof e === 'object' && 'name' in e && (e as { name: unknown }).name === 'AbortError';
}
